import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { Page, PageHeader, AddPageContent, Page404 } from '../src/ui'
import { Router as AdsAddRouter } from '../src/modules/ads/add/router'
import {
  fetchCategories,
  fetchCategoriesWithGroups,
  fetchCategory,
  fetchSpecifications,
  fetchAdditionalDependencies
} from '../src/modules/categories/actions'
import { fetchEquipment } from '../src/modules/ads/equipment/actions'
import { fetchBrands } from '../src/modules/ads/brands/actions'
import { fetchServices } from '../src/modules/ads/services/actions'
import { fetchCountries } from '../src/modules/geoObjects/countries/actions'
import { getIsAuthorized } from '../src/modules/users/Auth/selectors'
import { getUserInfo } from '../src/modules/users/Profile/selectors'
import { fetchCitiesByCountryWithinRegions } from '../src/modules/geoObjects/cities/actions'
import { fetchLocations } from '../src/modules/geoObjects/locations/actions'
import { getCurrentSubCategory } from '../src/modules/categories/selectors'
import initialState from '../src/modules/redux/initialState'
import { deepCopy } from '../src/utils'
import config from '../src/config/config.yaml'

class AddAdPage extends React.Component {
  static async getInitialProps ({store, req, query, isServer}) {
    const categorySeo = query.category

    let category = null
    let isPageExists = false
    let defaultSpecification = null

    await Promise.all([
      store.dispatch(fetchCategories()),
      store.dispatch(fetchCategoriesWithGroups())
    ])

    if (categorySeo) {
      const options = {seo: categorySeo, setCurrentSubCategory: true}

      await store.dispatch(fetchCategory(options, false))
      category = getCurrentSubCategory(store.getState())

      if (category && category.id) {
        isPageExists = true

        await Promise.all([
          store.dispatch(fetchSpecifications(category.id)),
          store.dispatch(fetchAdditionalDependencies(category.id)),
          store.dispatch(fetchEquipment(category.id)),
          store.dispatch(fetchBrands(category.id)),
          store.dispatch(fetchCountries()),
          store.dispatch(fetchServices(category))
        ])

        const isAuthorized = getIsAuthorized(store.getState())

        if (isAuthorized) {
          const userInfo = getUserInfo(store.getState())
          // Get geo data from user
          const userMainAddress = userInfo && Array.isArray(userInfo.userAddresses) ? userInfo.userAddresses[0] : null

          if (userMainAddress && userMainAddress.country && userMainAddress.city) {
            await Promise.all([
              store.dispatch(fetchCitiesByCountryWithinRegions(Number(userMainAddress.country.id))),
              store.dispatch(fetchLocations(Number(userMainAddress.city.id), category.id))
            ])
          }
        }
      }

      const defaultSpecificStr = isServer ? req.query.defaultSpecification : query.defaultSpecification

      if (defaultSpecificStr) {
        try {
          defaultSpecification = JSON.parse(defaultSpecificStr)
        } catch (err) {
          defaultSpecification = null
        }
      }
    } else {
      isPageExists = true
    }

    return {
      title: category && category.title
        ? `Dodavanje oglasa - ${category.title} | ${config.siteName}`
        : isPageExists ? `${config.pages.addAd.title} | ${config.siteName}` : config.pages.notFoundTitle,
      category: categorySeo,
      isPageExists,
      defaultSpecification,
      state: AddAdPage.getPageState(store, isPageExists)
    }
  }

  static getPageState (store, isPageExists = false) {
    const state = deepCopy(initialState)
    if (store) {
      const currentState = store.getState()

      state.user.auth.isAuthorized = currentState.user.auth.isAuthorized
      state.user.profile.error = currentState.user.profile.error
      state.user.profile.info = currentState.user.profile.info
      state.user.profile.favouriteAds = currentState.user.profile.favouriteAds
      state.user.profile.savedFilters = currentState.user.profile.savedFilters
      state.user.profile.favouriteProfiles = currentState.user.profile.favouriteProfiles

      state.common.transitions = currentState.common.transitions
      state.common.router = currentState.common.router
      state.common.is404 = !isPageExists
      state.common.userTypes = currentState.common.userTypes
      state.common.selectableUserTypes = currentState.common.selectableUserTypes
      state.common.isMobileDevice = currentState.common.isMobileDevice
      state.common.pageMetaTags = currentState.common.pageMetaTags
      state.common.pageMetaTagsSingle = currentState.common.pageMetaTagsSingle

      state.groups = currentState.groups

      state.categories.elements = currentState.categories.elements
      state.categories.bySeo = currentState.categories.bySeo
      state.categories.withGroups = currentState.categories.withGroups
      state.categories.parentCategories = currentState.categories.parentCategories

      if (isPageExists) {
        state.categories.currentSubcategory = currentState.categories.currentSubcategory
        state.categories.currentParentCategory = currentState.categories.currentParentCategory
        state.categories.specifications = currentState.categories.specifications
        state.categories.similarWords = currentState.categories.similarWords
        state.categories.additionalDependencies = currentState.categories.additionalDependencies
        state.categories.additionalDependenciesVisibility = currentState.categories.additionalDependenciesVisibility

        state.ads.equipment = currentState.ads.equipment
        state.ads.brands = currentState.ads.brands
        state.ads.services = currentState.ads.services

        state.ads.add.packages = currentState.ads.add.packages

        state.geoObjects.countries = currentState.geoObjects.countries
        state.geoObjects.regions = currentState.geoObjects.regions
        state.geoObjects.cities = currentState.geoObjects.cities
        state.geoObjects.locations = currentState.geoObjects.locations
        state.geoObjects.closerLocations = currentState.geoObjects.closerLocations
      }

      state.form = currentState.form

      state.cart = currentState.cart
      state.orders = currentState.orders
    }
    return state
  }

  render () {
    const { isPageExists, category, defaultSpecification } = this.props

    return isPageExists
      ? (
        <Page>
          <PageHeader>{config.pages.addAd.headerTitle}</PageHeader>

          <AddPageContent
            title={!category ? config.pages.addAd.title : 'Dodavanje oglasa'}
            description={
              category
                ? config.pages.addAd.description.form
                : config.pages.addAd.description.chooseCategory
            }
            descriptionClass={category ? 'heading-lead' : ''}
          >
            <AdsAddRouter
              category={category}
              defaultSpecification={defaultSpecification}
            />
          </AddPageContent>
        </Page>
      ) : <Page404 />
  }
}

AddAdPage.propTypes = {
  category: PropTypes.string,
  isPageExists: PropTypes.bool.isRequired,
  defaultSpecification: PropTypes.shape({
    id: PropTypes.number,
    value: PropTypes.string
  })
}

AddAdPage.defaultProps = {
  isPageExists: false
}

export default connect(null)(AddAdPage)
